import { ImageTypeEnum } from '@routellers/types/dist/upload/enum/image-type.enum';
import {
    ActionIcon,
    Box,
    Button,
    FileButton,
    Group,
    Image,
    Loader,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconTrash, IconUpload } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../../api';

import { CoverUpload } from './cover-upload';
import { ImageLightbox } from './image-lightbox';

type Props = {
    label?: string;
    value: string | null | undefined;
    onChange: (url: string | null) => void;
    kind?: 'image' | 'audio' | 'video' | 'auto';
    imageType?: ImageTypeEnum;
    disabled?: boolean;
    /** When false, hide paste-URL input — upload only. */
    allowUrl?: boolean;
};

const isAudioUrl = (url: string) => /\.(mp3|m4a|aac|ogg|opus|wav|flac)(\?|$)/i.test(url);
const isVideoUrl = (url: string) => /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url);

const acceptForKind = (kind: Props['kind']) => {
    if (kind === 'image') return 'image/*';
    if (kind === 'audio') return 'audio/*';
    if (kind === 'video') return 'video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov';

    return 'image/*,audio/*,video/*';
};

/** Audio / video / auto media. Images go through CoverUpload. */
const MediaDropzoneAv = ({
    label,
    value,
    onChange,
    kind = 'auto',
    imageType = ImageTypeEnum.POSTER,
    disabled,
    allowUrl = true,
}: Props) => {
    const { t } = useTranslation();
    const resolvedLabel = label ?? t('common.media');
    const [uploading, setUploading] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);

    const upload = async (file: File | null) => {
        if (!file) return;

        const extension = file.name.split('.').pop()?.toLowerCase() || '';
        const looksVideo =
            kind === 'video' ||
            (kind === 'auto' &&
                (file.type.startsWith('video/') ||
                    ['mp4', 'webm', 'mov', 'm4v'].includes(extension)));
        const looksAudio =
            kind === 'audio' ||
            (kind === 'auto' &&
                !looksVideo &&
                (file.type.startsWith('audio/') ||
                    ['mp3', 'm4a', 'aac', 'ogg', 'opus', 'wav', 'flac'].includes(extension)));
        const looksImage =
            kind === 'auto' &&
            !looksVideo &&
            !looksAudio &&
            (file.type.startsWith('image/') ||
                ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes(extension));

        if (!looksAudio && !looksImage && !looksVideo) {
            notifications.show({
                color: 'red',
                message: t('components.mediaDropzone.unsupportedFile'),
            });

            return;
        }

        setUploading(true);
        const data = new FormData();

        data.append('file', file);

        try {
            if (looksVideo) {
                const { data: res } = await api.uploads.uploadVideo({
                    data,
                    onUploadProgress: () => {},
                });

                onChange(res.url);
            } else if (looksAudio) {
                const { data: res } = await api.uploads.uploadAudio({
                    data,
                    onUploadProgress: () => {},
                });

                onChange(res.url);
            } else {
                data.append('type', imageType);
                const { data: res } = await api.uploads.uploadImage({
                    data,
                    onUploadProgress: () => {},
                });

                onChange(res.url);
            }
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setUploading(false);
        }
    };

    const showVideo = Boolean(value && (kind === 'video' || isVideoUrl(value)));
    const showAudio = Boolean(value && !showVideo && (kind === 'audio' || isAudioUrl(value)));
    const showImage = Boolean(value && !showVideo && !showAudio);

    return (
        <Stack gap='xs'>
            {resolvedLabel ? (
                <Text size='sm' fw={500}>
                    {resolvedLabel}
                </Text>
            ) : null}

            {value ? (
                <Box
                    p='sm'
                    style={{
                        border: '1px solid var(--mantine-color-gray-3)',
                        borderRadius: 'var(--mantine-radius-md)',
                    }}
                >
                    <Group justify='space-between' align='flex-start' wrap='nowrap'>
                        <Stack gap='xs' style={{ flex: 1, minWidth: 0 }}>
                            {showImage && (
                                <Image
                                    src={value}
                                    alt={resolvedLabel}
                                    mah={220}
                                    w='100%'
                                    fit='cover'
                                    radius='sm'
                                    style={{ cursor: 'zoom-in' }}
                                    onClick={() => setPreviewOpen(true)}
                                />
                            )}
                            {showVideo && (
                                // biome-ignore lint/a11y/useMediaCaption: admin preview only
                                <video
                                    controls
                                    src={value!}
                                    playsInline
                                    style={{
                                        width: '100%',
                                        maxHeight: 220,
                                        borderRadius: 6,
                                        background: '#000',
                                    }}
                                />
                            )}
                            {showAudio && (
                                // biome-ignore lint/a11y/useMediaCaption: admin preview only
                                <audio controls src={value!} style={{ width: '100%' }} />
                            )}
                        </Stack>
                        <Group gap={4}>
                            <FileButton
                                onChange={upload}
                                accept={acceptForKind(kind)}
                                disabled={disabled || uploading}
                            >
                                {(props) => (
                                    <ActionIcon
                                        {...props}
                                        variant='subtle'
                                        disabled={disabled || uploading}
                                        aria-label={t('components.mediaDropzone.replaceMedia')}
                                    >
                                        {uploading ? (
                                            <Loader size={14} />
                                        ) : (
                                            <IconUpload size={16} />
                                        )}
                                    </ActionIcon>
                                )}
                            </FileButton>
                            <ActionIcon
                                variant='subtle'
                                color='red'
                                disabled={disabled || uploading}
                                onClick={() => onChange(null)}
                                aria-label={t('components.mediaDropzone.removeMedia')}
                            >
                                <IconTrash size={16} />
                            </ActionIcon>
                        </Group>
                    </Group>
                </Box>
            ) : (
                <FileButton onChange={upload} accept={acceptForKind(kind)} disabled={disabled}>
                    {(props) => (
                        <Button
                            {...props}
                            variant='light'
                            leftSection={uploading ? <Loader size={14} /> : <IconUpload size={16} />}
                            disabled={disabled || uploading}
                        >
                            {t('components.mediaDropzone.uploadFile')}
                        </Button>
                    )}
                </FileButton>
            )}

            {allowUrl && (
                <TextInput
                    placeholder={t('components.mediaDropzone.orPasteUrl')}
                    value={value || ''}
                    disabled={disabled || uploading}
                    onChange={(e) => onChange(e.currentTarget.value.trim() || null)}
                />
            )}

            {showImage && value ? (
                <ImageLightbox
                    urls={[value]}
                    index={0}
                    opened={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                    title={resolvedLabel}
                />
            ) : null}
        </Stack>
    );
};

export const MediaDropzone = (props: Props) => {
    // Single image covers/posters share one upload + full-size preview UI.
    if (props.kind === 'image') {
        return (
            <CoverUpload
                label={props.label}
                value={props.value}
                onChange={props.onChange}
                imageType={props.imageType}
                disabled={props.disabled}
                allowUrl={props.allowUrl ?? true}
            />
        );
    }

    return <MediaDropzoneAv {...props} />;
};
